
import { useState } from "react"
import styled from "styled-components"
import { User, Mail, Edit, Key, Save, X, Check, ChevronDown, ChevronUp } from "lucide-react"

// Styled Components
const PageContainer = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1.5rem;
`

const AlertContainer = styled.div`
  background-color: ${(props) => (props.variant === "success" ? "#f0fdf4" : "#fef2f2")};
  border-left: 4px solid ${(props) => (props.variant === "success" ? "#22c55e" : "#ef4444")};
  color: ${(props) => (props.variant === "success" ? "#166534" : "#b91c1c")};
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
`

const AlertText = styled.p`
  margin: 0;
  font-size: 0.875rem;
`

const Card = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-bottom: 2rem;
`

const CardHeader = styled.div`
  padding: 1.5rem 1.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
`

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CardContent = styled.div`
  padding: 1.5rem;
`

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`

const Input = styled.input`
  width: 100%;
  max-width: 28rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 1rem;
  transition: border-color 0.15s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`

const FieldDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
`

const FieldValue = styled.span`
  font-size: 1.125rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.15s ease;
  
  background-color: ${(props) => {
    if (props.variant === "destructive") return "#ef4444"
    if (props.variant === "secondary") return "#f3f4f6"
    return "#3b82f6"
  }};
  
  color: ${(props) => {
    if (props.variant === "secondary") return "#111827"
    return "white"
  }};
  
  border: ${(props) => {
    if (props.variant === "secondary") return "1px solid #e5e7eb"
    return "none"
  }};
  
  &:hover {
    background-color: ${(props) => {
      if (props.variant === "destructive") return "#dc2626"
      if (props.variant === "secondary") return "#e5e7eb"
      return "#2563eb"
    }};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const OutlineButton = styled(Button)`
  background-color: transparent;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.1);
  }
`

const Separator = styled.hr`
  border: none;
  height: 1px;
  background-color: #e5e7eb;
  margin: 1.5rem 0;
`

const CollapsibleContainer = styled.div`
  margin-top: 1rem;
`

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const CollapsibleTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
`

const CollapsibleContent = styled.div`
  max-height: ${(props) => (props.isOpen ? "1000px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease;
`

// Mock auth context - replace with your actual auth context
const useAuth = () => {
  const [user, setUser] = useState({
    name: "홍길동",
    email: "user@example.com",
  })
  return { user, setUser }
}

export default function MyPage() {
  const { user, setUser } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState(user?.name || "")
  const [editedEmail, setEditedEmail] = useState(user?.email || "")
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [message, setMessage] = useState("")
  const [messageType, setMessageType] = useState("success")

  const handleEditProfile = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedName(user?.name || "")
    setEditedEmail(user?.email || "")
  }

  const handleSaveProfile = async () => {
    try {
      // API 호출로 프로필 업데이트 (실제 구현 필요)
      // Mock API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      // 사용자 정보 업데이트
      const updatedUser = { ...user, name: editedName, email: editedEmail }
      setUser(updatedUser)
      localStorage.setItem("user", JSON.stringify(updatedUser))
      sessionStorage.setItem("user", JSON.stringify(updatedUser))

      setIsEditing(false)
      setMessageType("success")
      setMessage("프로필이 성공적으로 업데이트되었습니다.")

      setTimeout(() => {
        setMessage("")
      }, 3000)
    } catch (error) {
      console.error("프로필 업데이트 오류:", error)
      setMessageType("error")
      setMessage("프로필 업데이트 중 오류가 발생했습니다.")
    }
  }

  const handleChangePassword = async () => {
    // 비밀번호 유효성 검사
    if (newPassword !== confirmPassword) {
      setMessageType("error")
      setMessage("새 비밀번호가 일치하지 않습니다.")
      return
    }

    try {
      // API 호출로 비밀번호 변경 (실제 구현 필요)
      // Mock API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
      setIsChangingPassword(false)
      setMessageType("success")
      setMessage("비밀번호가 성공적으로 변경되었습니다.")

      setTimeout(() => {
        setMessage("")
      }, 3000)
    } catch (error) {
      console.error("비밀번호 변경 오류:", error)
      setMessageType("error")
      setMessage("비밀번호 변경 중 오류가 발생했습니다.")
    }
  }

  const togglePasswordChange = () => {
    setIsChangingPassword(!isChangingPassword)
    if (isChangingPassword) {
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
    }
  }

  return (
    <PageContainer>
      <Title>마이페이지</Title>

      {message && (
        <AlertContainer variant={messageType}>
          {messageType === "success" && <Check size={16} style={{ marginRight: "0.5rem" }} />}
          <AlertText>{message}</AlertText>
        </AlertContainer>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <User size={20} />
            프로필 정보
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FormGroup>
            <Label htmlFor="name">이름</Label>
            {isEditing ? (
              <Input id="name" type="text" value={editedName} onChange={(e) => setEditedName(e.target.value)} />
            ) : (
              <FieldDisplay>
                <FieldValue>{user?.name || "(이름 정보 없음)"}</FieldValue>
                {!isEditing && (
                  <OutlineButton onClick={handleEditProfile}>
                    <Edit size={16} />
                    수정
                  </OutlineButton>
                )}
              </FieldDisplay>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">이메일</Label>
            {isEditing ? (
              <Input id="email" type="email" value={editedEmail} onChange={(e) => setEditedEmail(e.target.value)} />
            ) : (
              <FieldDisplay>
                <FieldValue>
                  <Mail size={16} style={{ color: "#6b7280" }} />
                  {user?.email || "(이메일 정보 없음)"}
                </FieldValue>
              </FieldDisplay>
            )}
          </FormGroup>

          {isEditing && (
            <ButtonGroup>
              <Button onClick={handleSaveProfile}>
                <Save size={16} />
                저장하기
              </Button>
              <Button variant="destructive" onClick={handleCancelEdit}>
                <X size={16} />
                취소
              </Button>
            </ButtonGroup>
          )}

          {!isEditing && (
            <>
              <Separator />

              <CollapsibleContainer>
                <CollapsibleHeader>
                  <Key size={20} />
                  <CollapsibleTitle>비밀번호 변경</CollapsibleTitle>
                </CollapsibleHeader>

                <Button variant={isChangingPassword ? "secondary" : "primary"} onClick={togglePasswordChange}>
                  {isChangingPassword ? (
                    <>
                      <ChevronUp size={16} />
                      취소
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} />
                      비밀번호 변경하기
                    </>
                  )}
                </Button>

                <CollapsibleContent isOpen={isChangingPassword}>
                  <FormGroup style={{ marginTop: "1rem" }}>
                    <Label htmlFor="current-password">현재 비밀번호</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="new-password">새 비밀번호</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="confirm-password">새 비밀번호 확인</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </FormGroup>

                  <Button onClick={handleChangePassword}>
                    <Save size={16} />
                    변경하기
                  </Button>
                </CollapsibleContent>
              </CollapsibleContainer>
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}

